import { Property } from 'csstype';
import React, { CSSProperties, FunctionComponent, PropsWithChildren } from 'react';
import styles from './styled-text-component.module.scss';

const fontSizes = ['sm', 'md', 'lg', 'xl', 'xxl', 'headline', 'paragraph', 'label'] as const;
type CommonFontSizeType = (typeof fontSizes)[number];

export type TagType = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type FontColor =
  | 'black'
  | 'grey'
  | 'white'
  | 'rsRed'
  | 'darkRed'
  | 'highlightRed'
  | 'midtoneRed'
  | 'shadowRed'
  | 'humanRed'
  | 'purple'
  | 'darkPurple'
  | string
  | null;

type StyledTextProps = PropsWithChildren<{
  /**
   * underlying html element
   */
  tagType?: TagType;

  size?: CommonFontSizeType | string;
  /** CSS.Property.FontWeight | { isBold: boolean } */
  weight?: Property.FontWeight | { isBold: boolean };
  /** CSS.Property.TextDecorationLine | { isStrikethrough?: boolean; isUnderline?: boolean } */
  decoration?: Property.TextDecoration | { isStrikethrough?: boolean; isUnderline?: boolean };
  /** CSS.Property.FontStyle | { isItalic: boolean } */
  styling?: Property.FontStyle | { isItalic: boolean };
  /** CSS TextColorProperty */
  color?: {
    desktop?: FontColor;
    mobile?: FontColor;
  };
  /** CSS TextAlignProperty */
  alignment?: {
    desktop?: Property.TextAlign | string | null;
    mobile?: Property.TextAlign | string | null;
  };
  padding?: {
    desktop?: {
      top?: string | null;
      bottom?: string | null;
      right?: string | null;
      left?: string | null;
    } | null;
    mobile?: {
      top?: string | null;
      bottom?: string | null;
      right?: string | null;
      left?: string | null;
    } | null;
  };
}>;

const DEFAULT_DECORATION = { isStrikethrough: false, isUnderline: false };
const DEFAULT_COLOR = { desktop: 'black', mobile: 'black' };
const DEFAULT_ALIGNMENT = { desktop: 'left', mobile: 'left' };
const DEFAULT_PADDING = {
  desktop: { top: '0', bottom: '0', left: '0', right: '0' },
  mobile: { top: '0', bottom: '0', left: '0', right: '0' },
};

export const StyledText: FunctionComponent<StyledTextProps> = ({
  tagType = 'p',
  size = 'sm',
  weight = 'normal',
  decoration = DEFAULT_DECORATION,
  styling = 'normal',
  color = DEFAULT_COLOR,
  alignment = DEFAULT_ALIGNMENT,
  padding = DEFAULT_PADDING,
  children,
  ...rest
}) => {
  const Tag = tagType || 'p';

  let textDecoration: Property.TextDecorationLine | undefined;

  if (typeof decoration === 'string') {
    textDecoration = decoration;
  } else if (decoration !== 0) {
    textDecoration = '';

    if (decoration?.isStrikethrough) {
      textDecoration += 'line-through';
    }
    if (decoration?.isUnderline) {
      textDecoration += ' underline';
    }
  }

  let textStyling: Property.FontStyle;

  if (typeof styling === 'string') {
    textStyling = styling;
  } else {
    textStyling = styling?.isItalic ? 'italic' : 'normal';
  }

  let fontWeight: Property.FontWeight | undefined;

  if (typeof weight === 'string' || typeof weight === 'number') {
    fontWeight = weight;
  } else {
    if (weight?.isBold === true) {
      fontWeight = 'bold';
    } else {
      fontWeight = undefined;
    }
  }

  function parseFontSize(checkFontSize: string | undefined): CommonFontSizeType {
    const fontSize = fontSizes.find((validName) => validName === checkFontSize);
    if (fontSize) {
      return fontSize;
    }
    return 'md';
  }

  return (
    <Tag
      {...rest}
      className={` ${styles['styled-text-component']} ${styles['text-size-' + parseFontSize(size)]} 
      ${styles[color?.desktop ?? '']} ${styles['mobile-' + (color?.mobile ?? '')]}`}
      style={
        {
          '--desktop-padding-top': padding?.desktop?.top + 'px',
          '--desktop-padding-right': padding?.desktop?.right + 'px',
          '--desktop-padding-bottom': padding?.desktop?.bottom + 'px',
          '--desktop-padding-left': padding?.desktop?.left + 'px',

          '--mobile-padding-top': padding?.mobile?.top + 'px',
          '--mobile-padding-right': padding?.mobile?.right + 'px',
          '--mobile-padding-bottom': padding?.mobile?.bottom + 'px',
          '--mobile-padding-left': padding?.mobile?.left + 'px',

          '--desktop-text-align': alignment?.desktop,
          '--mobile-text-align': alignment?.mobile,

          '--weight': fontWeight,

          textDecoration: textDecoration,
          fontStyle: textStyling,
        } as CSSProperties
      }
    >
      {children}
    </Tag>
  );
};

export default StyledText;
